import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

export default function Login() {
  const { user, loading: authLoading } = useAuth()
  const [modo, setModo] = useState('login')   // 'login' | 'signup'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [nombre, setNombre] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState(null)
  const [signupOk, setSignupOk] = useState(false)   // Mensaje "Confirmá tu email"
  const [betaCerrada, setBetaCerrada] = useState(true)  // Default seguro: cerrada hasta que sepamos
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next')
  const initialModo = params.get('modo')

  // Solo permitir signup si la beta está abierta
  useEffect(() => {
    if (initialModo === 'signup' && !betaCerrada) setModo('signup')
  }, [initialModo, betaCerrada])

  // Leer flag global de beta_cerrada desde app_config
  useEffect(() => {
    let cancel = false
    ;(async () => {
      const { data } = await supabase
        .from('app_config')
        .select('value')
        .eq('key', 'beta_cerrada')
        .maybeSingle()
      if (cancel) return
      const cerrada = data?.value === 'true' || data?.value === true || data?.value === '"true"'
      setBetaCerrada(cerrada)
      // Si está cerrada y modo=signup, forzar a login
      if (cerrada) setModo('login')
    })()
    return () => { cancel = true }
  }, [])

  // Si ya hay sesión activa → redirigir
  useEffect(() => {
    if (!authLoading && user) {
      navigate(next || '/', { replace: true })
    }
  }, [user, authLoading, next, navigate])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    if (modo === 'signup') {
      // Signup con email verification (si Supabase lo tiene activado)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { nombre },
          emailRedirectTo: `${window.location.origin}${next || '/cliente'}`,
        },
      })
      if (error) {
        setError(traducirError(error.message))
        setLoading(false)
        return
      }
      // Si la sesion es null, esta esperando confirmacion de email
      if (!data?.session) {
        setSignupOk(true)
        setLoading(false)
        return
      }
      // Si ya hay sesion (email auto-confirmado en config), redirigir
      navigate(next || '/', { replace: true })
      return
    }
    // Login normal
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError(traducirError(error.message))
      setLoading(false)
      return
    }
    navigate(next || '/', { replace: true })
  }

  async function handleGoogle() {
    setError(null)
    setGoogleLoading(true)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${next || '/'}`,
      },
    })
    if (error) {
      setError(error.message)
      setGoogleLoading(false)
    }
  }

  return (
    <div className="lp-login">
      {/* Navbar simple igual que la web pública */}
      <nav className="lp-login-nav">
        <div className="lp-login-nav-inner">
          <a href="https://limpack.uy" className="lp-login-logo">Lim<span>pack</span></a>
          {betaCerrada ? (
            <span className="lp-login-nav-link" style={{ color: '#fff', fontSize: 13, opacity: 0.85 }}>
              🔒 Beta cerrada
            </span>
          ) : modo === 'login' ? (
            <button type="button" className="lp-login-nav-link" onClick={() => { setModo('signup'); setError(null) }} style={{ background: 'transparent', border: 0, cursor: 'pointer', font: 'inherit', color: '#fff' }}>
              ¿No tenés cuenta? <strong>Crear cuenta</strong>
            </button>
          ) : (
            <button type="button" className="lp-login-nav-link" onClick={() => { setModo('login'); setError(null); setSignupOk(false) }} style={{ background: 'transparent', border: 0, cursor: 'pointer', font: 'inherit', color: '#fff' }}>
              ¿Ya tenés cuenta? <strong>Entrar</strong>
            </button>
          )}
        </div>
      </nav>

      <main className="lp-login-main">
        <div className="lp-login-card">
          {/* Isotipo Limpack (la gota) */}
          <div className="lp-login-iso">
            <img src="/isotipo.png" alt="Limpack" />
          </div>

          <div className="lp-login-head">
            {signupOk ? (
              <>
                <h1>📧 Revisá tu email</h1>
                <p>Te mandamos un link a <strong>{email}</strong> para confirmar tu cuenta. Hacé click ahí y volvé.</p>
              </>
            ) : modo === 'signup' ? (
              <>
                <h1>Crear cuenta</h1>
                <p>Sumate a Limpack en 30 segundos. Te mandamos un email de confirmación.</p>
              </>
            ) : (
              <>
                <h1>Ingresá a tu cuenta</h1>
                <p>{betaCerrada ? 'Beta cerrada — solo invitados.' : 'Clientes, porteros y equipo Limpack.'}</p>
              </>
            )}
          </div>

          {betaCerrada && !signupOk && (
            <div style={{ padding: 14, background: '#fff8e6', border: '1px solid #fcd34d', borderRadius: 10, color: '#78350f', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
              <strong>🔒 Estamos en beta cerrada.</strong><br/>
              El registro está restringido a usuarios invitados. Si querés ser parte, escribinos al{' '}
              <a href="https://wa.me/59898610001" target="_blank" rel="noopener" style={{ color: '#78350f', fontWeight: 700 }}>WhatsApp 098 610 001</a>.
            </div>
          )}

          {signupOk && (
            <div style={{ padding: 16, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, color: '#15803d', fontSize: 14, lineHeight: 1.5, textAlign: 'center', marginTop: 8 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>✉️</div>
              Te enviamos un email a <strong>{email}</strong>.<br/>
              Click en el link para activar tu cuenta y entrar.
              <div style={{ marginTop: 14, fontSize: 12, color: '#15803d' }}>
                ¿No te llegó? Revisá spam o{' '}
                <button type="button" onClick={() => { setSignupOk(false) }} style={{ background: 'transparent', border: 0, color: '#15803d', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                  intentá de nuevo
                </button>
              </div>
            </div>
          )}

          {!signupOk && (<>
          {/* Google OAuth */}
          <button
            type="button"
            onClick={handleGoogle}
            disabled={googleLoading}
            className="lp-btn-google"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            {googleLoading ? 'Abriendo Google…' : 'Continuar con Google'}
          </button>

          <div className="lp-divider"><span>o con email</span></div>

          <form onSubmit={handleSubmit} className="lp-login-form">
            {modo === 'signup' && (
              <label className="lp-login-field">
                <b>Nombre y apellido</b>
                <input
                  type="text"
                  required
                  autoComplete="name"
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                  placeholder="Juan Pérez"
                />
              </label>
            )}
            <label className="lp-login-field">
              <b>Email</b>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="tu@email.com"
              />
            </label>

            <label className="lp-login-field">
              <b>Contraseña</b>
              <div className="lp-login-pw-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="lp-login-pw-toggle"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </label>

            {error && <div className="lp-login-err">{error}</div>}

            <button type="submit" disabled={loading} className="lp-btn-entrar">
              {loading
                ? (modo === 'signup' ? 'Creando…' : 'Entrando…')
                : (modo === 'signup' ? 'Crear cuenta' : 'Entrar')}
            </button>
          </form>

          {modo === 'login' && (
            <div className="lp-login-foot">
              <Link to="/reset-password">¿Olvidaste tu contraseña?</Link>
            </div>
          )}
          </>)}
        </div>

        <p className="lp-login-help">
          ¿Necesitás ayuda? Escribinos al{' '}
          <a href="https://wa.me/59898610001" target="_blank" rel="noopener">WhatsApp 098 610 001</a>
        </p>
      </main>

      <style>{styles}</style>
    </div>
  )
}

function traducirError(msg) {
  if (/invalid login credentials/i.test(msg)) return 'Email o contraseña incorrectos.'
  if (/email not confirmed/i.test(msg)) return 'Tenés que confirmar tu email primero. Revisá tu bandeja de entrada.'
  if (/user not found/i.test(msg)) return 'No encontramos una cuenta con ese email.'
  return msg
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00c4d4;
  --cream: #F8FAFC;
  --line2: #e2e8f0;
}
html, body { margin: 0; background: var(--navy) }
.lp-login {
  min-height: 100vh;
  position: relative;
  background: linear-gradient(135deg, #0d1f3d 0%, #1a3461 45%, #1a5cb8 100%);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--navy);
  -webkit-font-smoothing: antialiased;
  display: flex; flex-direction: column;
  overflow: hidden;
}
/* Halos turquesa decorativos (mismo lenguaje que el hero de la web) */
.lp-login::before, .lp-login::after {
  content: ''; position: absolute; pointer-events: none;
  border-radius: 50%; filter: blur(80px);
}
.lp-login::before {
  width: 420px; height: 420px;
  top: -120px; right: -120px;
  background: radial-gradient(circle, rgba(0,196,212,.45) 0%, rgba(0,196,212,0) 70%);
}
.lp-login::after {
  width: 380px; height: 380px;
  bottom: -100px; left: -120px;
  background: radial-gradient(circle, rgba(26,92,184,.55) 0%, rgba(26,92,184,0) 70%);
}

/* Navbar */
.lp-login-nav {
  position: relative; z-index: 2;
  background: rgba(13,31,61,.4);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.lp-login-nav-inner {
  max-width: 1100px; margin: 0 auto;
  padding: 16px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
}
.lp-login-logo {
  font-size: 22px; font-weight: 900;
  color: #fff; text-decoration: none;
  letter-spacing: -.02em;
}
.lp-login-logo span { color: var(--teal) }
.lp-login-nav-link {
  font-size: 13px; color: rgba(255,255,255,.65); text-decoration: none;
  transition: color .15s;
}
.lp-login-nav-link strong { color: var(--teal); font-weight: 700 }
.lp-login-nav-link:hover { color: #fff }

/* Main */
.lp-login-main {
  position: relative; z-index: 2;
  flex: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 40px 16px;
  gap: 18px;
}

/* Card */
.lp-login-card {
  background: #fff;
  width: 100%; max-width: 420px;
  border-radius: 20px;
  padding: 36px 28px;
  box-shadow: 0 8px 40px rgba(26,52,97,.10);
  box-sizing: border-box;
}
@media (min-width: 480px) {
  .lp-login-card { padding: 40px 36px }
}

/* Isotipo Limpack (la gota) */
.lp-login-iso {
  width: 76px; height: 76px;
  margin: 0 auto 18px;
  display: flex; align-items: center; justify-content: center;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 8px 24px rgba(0,196,212,.2), 0 0 0 1px rgba(255,255,255,.6);
}
.lp-login-iso img {
  width: 56px; height: 56px;
  object-fit: contain;
  display: block;
}

/* Header */
.lp-login-head { text-align: center; margin-bottom: 24px }
.lp-login-head h1 {
  font-size: 24px; font-weight: 900;
  color: var(--navy); margin: 0 0 6px;
  letter-spacing: -.01em;
}
.lp-login-head p {
  font-size: 13px; color: #64748b; margin: 0;
}

/* Botón Google */
.lp-btn-google {
  width: 100%;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  background: #fff; color: var(--navy);
  border: 1.5px solid var(--line2);
  padding: 12px 16px;
  border-radius: 10px;
  font-weight: 600; font-size: 14px;
  cursor: pointer; font-family: inherit;
  transition: background .15s, border-color .15s;
  box-sizing: border-box;
}
.lp-btn-google:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}
.lp-btn-google:disabled { opacity: .6; cursor: wait }

/* Divider */
.lp-divider {
  display: flex; align-items: center; gap: 12px;
  color: #94a3b8; font-size: 12px;
  margin: 18px 0;
}
.lp-divider span { white-space: nowrap }
.lp-divider::before, .lp-divider::after {
  content: ''; flex: 1; height: 1px; background: var(--line2);
}

/* Form */
.lp-login-form { display: flex; flex-direction: column; gap: 14px }
.lp-login-field { display: block }
.lp-login-field b {
  display: block; font-size: 11px; font-weight: 800;
  color: var(--navy); letter-spacing: .08em;
  text-transform: uppercase; margin-bottom: 6px;
}
.lp-login-field input {
  width: 100%; box-sizing: border-box;
  padding: 12px 14px; font-size: 15px;
  border: 1.5px solid var(--line2); border-radius: 10px;
  background: #fff; color: var(--navy); font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.lp-login-field input:focus {
  outline: none;
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(0,196,212,.15);
}

/* Password toggle */
.lp-login-pw-wrap { position: relative }
.lp-login-pw-wrap input { padding-right: 44px }
.lp-login-pw-toggle {
  position: absolute; right: 8px; top: 50%;
  transform: translateY(-50%);
  background: none; border: 0;
  padding: 6px 8px; font-size: 16px;
  cursor: pointer; color: #94a3b8;
}
.lp-login-pw-toggle:hover { color: var(--blue) }

/* Error */
.lp-login-err {
  background: #fef2f2; border: 1px solid #fca5a5;
  color: #dc2626; border-radius: 10px;
  padding: 10px 14px; font-size: 13px;
}

/* Botón Entrar */
.lp-btn-entrar {
  width: 100%;
  background: linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%);
  color: #fff; font-weight: 700; font-size: 15px;
  padding: 14px 20px; border: 0; border-radius: 10px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 14px rgba(0,196,212,.25);
  transition: transform .12s, box-shadow .12s, opacity .15s;
  margin-top: 4px;
}
.lp-btn-entrar:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0,196,212,.35);
}
.lp-btn-entrar:disabled { opacity: .6; cursor: wait }

/* Footer del card */
.lp-login-foot {
  margin-top: 22px; padding-top: 18px;
  border-top: 1px solid var(--line2);
  text-align: center;
}
.lp-login-foot a {
  color: #64748b; text-decoration: none;
  font-size: 13px; font-weight: 600;
  transition: color .15s;
}
.lp-login-foot a:hover { color: var(--blue); text-decoration: underline }

/* Help link debajo del card */
.lp-login-help {
  font-size: 12px; color: rgba(255,255,255,.55);
  text-align: center; margin: 0;
}
.lp-login-help a {
  color: var(--teal); text-decoration: none; font-weight: 600;
}
.lp-login-help a:hover { color: #fff; text-decoration: underline }
`
